import { isActiveFilterValue, NO_FILTER_VALUE } from "@/lib/utils/filter-utils";

export interface FilterVisibilityCatalogItem {
  id: string;
  type: "select" | "text" | "daterange";
  multiple?: boolean;
  /** When false, the chip is off until the user enables it. Defaults to true. */
  defaultVisible?: boolean;
}

export type FilterFieldValue = string | string[] | { from: string; to: string };

/**
 * Reconcile enabled filter IDs when the catalog changes.
 *
 * - Drops IDs that left the catalog
 * - Enables newly added catalog IDs that are default-visible
 * - Keeps user deselections for IDs that remain
 */
export function syncEnabledFilterIds(
  prevEnabled: readonly string[],
  prevCatalogIds: readonly string[],
  nextCatalog: ReadonlyArray<FilterVisibilityCatalogItem>,
): string[] {
  const nextCatalogIds = nextCatalog.map((filter) => filter.id);
  const nextSet = new Set(nextCatalogIds);
  const prevCatalogSet = new Set(prevCatalogIds);
  const kept = prevEnabled.filter((id) => nextSet.has(id));
  const keptSet = new Set(kept);
  const newlyAdded = nextCatalog
    .filter(
      (filter) =>
        !prevCatalogSet.has(filter.id) &&
        !keptSet.has(filter.id) &&
        filter.defaultVisible !== false,
    )
    .map((filter) => filter.id);
  return [...kept, ...newlyAdded];
}

export function emptyValueForFilter(
  filter: FilterVisibilityCatalogItem,
): FilterFieldValue {
  if (filter.type === "daterange") {
    return { from: "", to: "" };
  }
  if (filter.multiple) {
    return [];
  }
  return NO_FILTER_VALUE;
}

export function catalogIdsOf(
  filters: ReadonlyArray<{ id: string }> | undefined,
): string[] {
  return filters?.map((filter) => filter.id) ?? [];
}

export function defaultEnabledIdsOf(
  filters: ReadonlyArray<FilterVisibilityCatalogItem> | undefined,
): string[] {
  return (
    filters
      ?.filter((filter) => filter.defaultVisible !== false)
      .map((filter) => filter.id) ?? []
  );
}

/** IDs of catalog filters that currently have a non-empty value applied. */
export function appliedFilterIdsOf(
  catalog: ReadonlyArray<{ id: string }> | undefined,
  values: Record<string, FilterFieldValue>,
): string[] {
  if (!catalog) return [];
  return catalog
    .filter((filter) => isActiveFilterValue(values[filter.id]))
    .map((filter) => filter.id);
}

/**
 * Union user-enabled chips with filters that currently have a value.
 * Values persist on the toolbar; visibility is stored separately when
 * the table passes a `storageKey`.
 */
export function withAppliedEnabledIds(
  enabledIds: readonly string[],
  appliedIds: readonly string[],
): readonly string[] {
  if (appliedIds.length === 0) return enabledIds;
  const enabledSet = new Set(enabledIds);
  const missing = appliedIds.filter((id) => !enabledSet.has(id));
  if (missing.length === 0) return enabledIds;
  return [...enabledIds, ...missing];
}
